import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { createCorporate } from "../../../Services/adminCorporates";
import amplitudeService from "../../../Services/amplitude";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Switch } from "../../../ui/switch";
import { Separator } from "../../../ui/separator";
import {
  createCorporateSchema as schema,
  SLUG_REGEX as slugRegex,
  DOMAIN_REGEX as domainRegex,
  toCorporatePayload,
} from "./schemas";

const Field = ({ label, htmlFor, error, hint, children, required }) => (
  <div className="grid gap-1.5">
    <Label htmlFor={htmlFor}>
      {label}
      {required && <span className="text-destructive ml-1">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    {error && (
      <p className="text-xs text-destructive" role="alert">
        {error}
      </p>
    )}
  </div>
);

const AdminCorporateCreate = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState(null);
  const [domainInput, setDomainInput] = useState("");

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      display_name: "",
      slug: "",
      verification_email_domains: [],
      primary_color: "",
      secondary_color: "",
      industry: "",
      timezone: "",
      support_email: "",
      support_phone: "",
      account_owner_email: "",
      contract_seat_limit: "",
      contract_start_date: "",
      contract_end_date: "",
      allow_personal_email_login: true,
      default_subscription_plan_id: "",
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "verification_email_domains",
  });

  const slug = watch("slug");

  const handleAddDomain = () => {
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) return;
    if (!domainRegex.test(trimmed)) {
      setError("verification_email_domains", {
        message: "Enter a valid domain (e.g. company.com)",
      });
      return;
    }
    if (fields.some((f) => f.value === trimmed)) {
      setDomainInput("");
      return;
    }
    append({ value: trimmed });
    setDomainInput("");
  };

  const onSubmit = async (values) => {
    setSubmitError(null);

    const payload = toCorporatePayload(values);

    try {
      const created = await createCorporate(payload);
      amplitudeService.track?.("admin_corporate_created", {
        slug: created?.slug,
        corporate_id: created?.id,
      });
      navigate(`/admin/enterprises/${created?.id}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || "";
      if (status === 409 || /slug.*in.use/i.test(message)) {
        setError("slug", { message: "This slug is already in use." });
      } else {
        setSubmitError(message || "Failed to create enterprise. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/enterprises")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">New enterprise</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Name"
              htmlFor="name"
              required
              error={errors.name?.message}
            >
              <Input id="name" {...register("name")} />
            </Field>
            <Field
              label="Display name"
              htmlFor="display_name"
              hint="Defaults to Name if left blank."
              error={errors.display_name?.message}
            >
              <Input id="display_name" {...register("display_name")} />
            </Field>
            <Field
              label="Slug"
              htmlFor="slug"
              required
              hint={
                slug && slugRegex.test(slug)
                  ? `Activation URL: /enterprise/${slug}`
                  : "Lowercase letters, numbers, and hyphens. 1–64 chars. Cannot be changed after creation."
              }
              error={errors.slug?.message}
            >
              <Input
                id="slug"
                {...register("slug")}
                onChange={(e) => {
                  // Auto-lowercase as the admin types — slugs are always lowercase.
                  setValue("slug", e.target.value.toLowerCase(), {
                    shouldValidate: true,
                  });
                }}
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Eligibility</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Verification email domains"
              htmlFor="domain-input"
              hint="Press Enter or click Add to include each domain."
              error={errors.verification_email_domains?.message}
            >
              <div className="flex gap-2">
                <Input
                  id="domain-input"
                  placeholder="company.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddDomain();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={handleAddDomain}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {fields.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {fields.map((field, idx) => (
                    <span
                      key={field.id}
                      className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs"
                    >
                      {field.value}
                      <button
                        type="button"
                        onClick={() => remove(idx)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Branding (optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Primary color"
                htmlFor="primary_color"
                error={errors.primary_color?.message}
                hint="Hex like #0d5eaf"
              >
                <Input
                  id="primary_color"
                  placeholder="#0d5eaf"
                  {...register("primary_color")}
                />
              </Field>
              <Field
                label="Secondary color"
                htmlFor="secondary_color"
                error={errors.secondary_color?.message}
                hint="Hex like #4377fc"
              >
                <Input
                  id="secondary_color"
                  placeholder="#4377fc"
                  {...register("secondary_color")}
                />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">
              Logo can be uploaded after creation from the Branding tab.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profile (optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Industry"
              htmlFor="industry"
              error={errors.industry?.message}
            >
              <Input id="industry" {...register("industry")} />
            </Field>
            <Field
              label="Timezone"
              htmlFor="timezone"
              hint="IANA zone, e.g. America/New_York"
              error={errors.timezone?.message}
            >
              <Input id="timezone" {...register("timezone")} />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contacts (optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Support email"
              htmlFor="support_email"
              error={errors.support_email?.message}
            >
              <Input
                id="support_email"
                type="email"
                {...register("support_email")}
              />
            </Field>
            <Field
              label="Support phone"
              htmlFor="support_phone"
              error={errors.support_phone?.message}
            >
              <Input id="support_phone" {...register("support_phone")} />
            </Field>
            <Field
              label="Account owner email"
              htmlFor="account_owner_email"
              error={errors.account_owner_email?.message}
            >
              <Input
                id="account_owner_email"
                type="email"
                {...register("account_owner_email")}
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contract (optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Seat limit"
              htmlFor="contract_seat_limit"
              hint="0 means no cap."
              error={errors.contract_seat_limit?.message}
            >
              <Input
                id="contract_seat_limit"
                type="number"
                min={0}
                {...register("contract_seat_limit")}
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Start date"
                htmlFor="contract_start_date"
                error={errors.contract_start_date?.message}
              >
                <Input
                  id="contract_start_date"
                  type="date"
                  {...register("contract_start_date")}
                />
              </Field>
              <Field
                label="End date"
                htmlFor="contract_end_date"
                error={errors.contract_end_date?.message}
              >
                <Input
                  id="contract_end_date"
                  type="date"
                  {...register("contract_end_date")}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Auth & subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">
                  Allow personal-email login
                </Label>
                <p className="text-xs text-muted-foreground">
                  Members can log in with either their work or personal email.
                </p>
              </div>
              <Switch
                checked={watch("allow_personal_email_login")}
                onCheckedChange={(checked) =>
                  setValue("allow_personal_email_login", checked)
                }
              />
            </div>
            <Separator />
            <Field
              label="Default plan template id"
              htmlFor="default_subscription_plan_id"
              hint="Optional — pulled from subscription-lambda."
              error={errors.default_subscription_plan_id?.message}
            >
              <Input
                id="default_subscription_plan_id"
                {...register("default_subscription_plan_id")}
              />
            </Field>
          </CardContent>
        </Card>

        {submitError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/enterprises")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create enterprise"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminCorporateCreate;
